<template>
  <div :class="['chatbox', { trainer: isTrainer }]" :style="{ height: wrapperHeight }">
    <perfect-scrollbar class="message-wrapper">
      <!-- Chat items -->
      <ChatMessageItems
        class="chat_message_items"
        :message-list="messageList"
        :remote-seen="remoteSeen"
        :disable="false"
        @deleteMessage="deleteMessage"
        @editMessage="editMessage"
      />
      <!-- Chat items end -->

      <!-- Temp Message (Sending and Failed) -->
      <ChatTempMessage :temp-message-list="tempMessageList" />
      <!-- Temp Message End -->

      <!-- Typing Message -->
      <div v-show="remoteTyping" class="notranslate typing received-message">
        {{ remoteNickname }} is typing
        <div class="loadingDots">
          <div class="dot1"></div>
          <div class="dot2"></div>
          <div class="dot3"></div>
        </div>
      </div>
      <!-- Typing Message End -->
    </perfect-scrollbar>

    <!-- Input Message -->
    <span v-if="showMessageNotif" class="notification" @click="scrollToEnd">{{
      $t('chatNotification')
    }}</span>
    <div id="chat-input-warning" v-if="showWarningMessage">
      <img src="@/assets/img/info_icon.svg" />
      <span id="chat-input-message">{{ $t('chatUnavailable') }}</span>
    </div>
    
    <!-- Useful phrases -->
    <template v-if="!isCmsPreview && !hideUsefulPhrase">
      <Phrases
        ref="phraseModal"
        @phraseMessage="phraseMessage"
        @phraseCloseDialog="isPhraseActive = false"
      />
      <div class="tooltip">
        <div :class="tooltipTextClass" v-show="tooltipvisible">
          <div style="text-align: right; margin: 0">
            <i class="el-alert__closebtn el-icon-close" @click="tooltipvisible = false;" style="font-size: large;"></i>
          </div>
          <p class="text-primary chatSupportTextClass">{{ $t('chatSupportText') }}</p>
          <p class="messageSendTextClass">{{ $t('messageSendText') }}</p>
          <el-button
            class="btn-click" 
            style="margin-top: 5px; margin-bottom: 5px;"
            v-on="isUsefulPhraseClickable ? { click: togglePhraseDialog } : {}" 
            @click="tooltipvisible = false;">
              <strong>{{ $t('clickHereText') }}</strong>
          </el-button>
        </div>
      </div>
    </template>
    <div :class="['rte-input', { disabled: disableInput }]">
      <a
        v-if="!hideUsefulPhrase"
        :class="['phrases-dots', usefulPhraseClass]"
        @click="toggleTooltipVisible"
        v-on="isUsefulPhraseClickable ? { click: togglePhraseDialog } : {}"
      >
        <img src="@/assets/img/speech_bubble.svg" />
      </a>
      <a
        :class="['toolbar-dots', { active: isToolbarDisplayed }]"
        v-on="!disableInput ? { click: toogleToolbar } : {}"
      >
        <span>Aa</span>
      </a>
      <vue-editor
        ref="editor"
        v-model="chatMessage"
        :editor-toolbar="customToolbar"
        :editorOptions="editorSettings"
        :disabled="disableInput"
        @keydown.enter.native="checkSendKey"
      />

      <!-- Chat message tagging -->
      <TrainerTag
        v-if="enableTrainerTag"
        ref="trainerTag"
        :chat-message="chatMessage"
        @setDialogPosition="setTagDialogPosition()"
      />

      <a :class="[ 'btn-chat', { disabled:disabledChat } ]" v-if="!pendingEdit || isEditingActive" @click="sendMessage">
        <img v-if="this.connected && !this.voluntaryLessonTermination" src="@/assets/img/send_icn-active.svg"/>
        <img v-else src="@/assets/img/send_icn-disabled.svg"/>
      </a>
      <a v-else id="btn-cancel-edit" @click="cancelEdit">
        <i class="el-icon-close"></i>
      </a>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';

import uuid from 'uuid/v4';
import { VueEditor } from 'vue2-editor';
import { debounce, store } from '@/utils/common';
import {
  TrainerTagModule,
  ActiveTrainerTag,
  HighlightTrainerTag,
  PhraseNonEditable
} from '@/utils/rteModule';

import ChatMessageItems from '@/components/Lesson/Chatbox/ChatMessageItems';
import ChatTempMessage from '@/components/Lesson/Chatbox/ChatTempMessage';
import Phrases from '@/components/Lesson/Chatbox/Phrases';
import TrainerTag from '@/components/Lesson/Chatbox/TrainerTag';
import cmsPreview from '@/mixins/cmsPreview';

export default {
  name: 'ChatBox',
  mixins: [cmsPreview],
  components: {
    ChatMessageItems,
    ChatTempMessage,
    VueEditor,
    Phrases,
    TrainerTag
  },
  props: ['setTagDialogPosition'],
  data: () => {
    return {
      customToolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: ['#000000', '#00A4D9', '#36B37E', '#FF5630', '#FF991F'] }]
      ],
      isToolbarDisplayed: false,
      editorSettings: {
        TrainerTagModule,
        ActiveTrainerTag,
        HighlightTrainerTag,
        PhraseNonEditable
      },

      // input field binding
      chatMessage: '',

      // message history/list
      tempMessageList: [],

      // uuid library instance
      uuid: uuid,

      // notification flags
      localTyping: false,
      localTypingTimer: null,
      isScrollAtBottom: false,
      showMessageNotif: false,

      logger: null,
      wrapperHeight: 0,
      initialChatHeight: null,
      showColors: false,

      // message to be edit
      pendingEdit: null,
      isEditingActive: false,

      // usefulphrase
      isPhraseActive: false,
      showPulseEffect: false,
      tooltipvisible: true,
      tooltipTextClass: null,

      // track IME composition phase
      isIMEComposing: false
    };
  },
  computed: {
    ...mapState('connection', ['canReconnect']),
    ...mapState('lesson', ['isLessonBecameOngoing', 'isLessonBeyondGracePeriod', 'isLessonBeyondChatPeriod']),
    ...mapState('socketio', [
      'socket',
      'connected',
      'onConnect',
      'messageList',
      'remoteTyping',
      'remoteSeen',
      'voluntaryLessonTermination',
      'totalUnseen'
    ]),
    ...mapState(['lessonToken', 'lessonId', 'localNickname', 'remoteNickname', 'accType', 'userId']),
    ...mapState('trainerTag', ['trainerTags']),
    ...mapState(['isTrial']),
    ...mapGetters([
      'isLessonOngoing',
      'isTrainer',
      'isStudent',
      'isWaitingForLesson',
      'isWaitingOrWarmUpPeriod',
      'isLessonEnded',
      'nonActualLesson',
      'isZipanService',
      'isAssessmentLesson'
    ]),
    ...mapGetters('userOtherSetting', ['isTaiwanCountryId', 'isRemoteStudentTaiwan']),

    // count number of failed messages
    failedCount: function() {
      let count = 0;
      this.tempMessageList.forEach(function(item) {
        if (item.failed) {
          count++;
        }
      });
      return count;
    },

    showWarningMessage() {
      return !this.connected && !this.isLessonBeyondGracePeriod && this.isLessonOngoing;
    },

    isOriginalMessageChange() {
      return this.chatMessage != this.pendingEdit.m;
    },

    disabledChat() {
      return (
        this.isLessonBeyondChatPeriod ||
        this.isWaitingForLesson ||
        this.voluntaryLessonTermination ||
        !this.connected
      );
    },

    lessonHasEnded() {
      return this.isLessonBeyondGracePeriod || this.voluntaryLessonTermination;
    },

    disableInput() {
      return !this.connected && this.isTrial;
    },

    usefulPhraseClass() {
      return {
        disabled: this.isCmsPreview,
        active: this.isPhraseActive,
        pulse: this.showPulseEffect,
      };
    },

    isUsefulPhraseClickable() {
      return !this.isCmsPreview && !this.disableInput
    },

    hideUsefulPhrase() {
      return this.isTaiwanCountryId || this.isRemoteStudentTaiwan || this.isZipanService;
    },

    enableTrainerTag() {
      return this.isTrainer && !this.nonActualLesson && !this.isRemoteStudentTaiwan && !this.isZipanService;
    },

    isAssessmentStudent() {
      return this.isAssessmentLesson && this.isStudent
    },

    shouldShowTooltip() {
      if (this.isLessonEnded) {
        return false;
      }

      if (this.isAssessmentStudent && this.isWaitingOrWarmUpPeriod) {
        return false;
      }

      return true;
    }
  },

  beforeMount() {
    if (this.onConnect) {
      this.enablingChat();
    }
  },

  mounted() {
    document.getElementsByClassName('ql-bold')[0].innerHTML =
      '<img width="10" height="15" src="' + require('@/assets/img/b@3x.png') + '" />';
    document.getElementsByClassName('ql-italic')[0].innerHTML =
      '<img width="7" height="15" src="' + require('@/assets/img/i@3x.png') + '" />';
    document.getElementsByClassName('ql-underline')[0].innerHTML =
      '<img width="12" height="15" src="' + require('@/assets/img/u@3x.png') + '" />';
    document.getElementsByClassName('ql-strike')[0].innerHTML =
      '<img width="13" height="15" src="' + require('@/assets/img/s@3x.png') + '" />';
    document.querySelector('.ql-editor').classList.add('scrollbar');

    document.querySelector('.ql-picker').addEventListener('click', debounce(this.toggleColor, 50));
    document.querySelector('.ql-container').addEventListener('click', this.addSpanElement);
    this.setPasteContentAsPlain();

    let vm = this;

    // bind enter key to prevent the highlighted text being deleted on enter
    this.$refs.editor.quill.keyboard.bindings[13].unshift(
      this.$refs.editor.quill.keyboard.bindings[13].pop()
    );

    // listener to track IME composition
    const editorEl = this.$refs.editor.$el.querySelector('.ql-editor');
    editorEl.addEventListener('compositionstart', () => {
      this.isIMEComposing = true;
    });
    editorEl.addEventListener('compositionend', () => {
      setTimeout(() => this.isIMEComposing = false, 10)
    });
    // For safari, as composition listener has a different timing
    editorEl.addEventListener("keydown", (e) => {
      if (e.isComposing || e.keyCode === 229) {
        this.isIMEComposing = true;
        return; 
      }
      if (e.key === 'Enter') {
        this.isIMEComposing = false;
      }
    });

    this.$refs.phraseModal?.quillBindings();

    document.body.addEventListener('click', function(event) {
      let ignore = document.querySelector('.ql-color');
      if (event.target === ignore || ignore.contains(event.target)) {
        return;
      }

      document.querySelector('.ql-snow.ql-toolbar').classList.remove('showColors');
      vm.showColors = false;
    });

    document.querySelector('.ql-color-picker').addEventListener('keydown', function(event) {
      if (event.keyCode == 13 || event.keyCode == 27) {
        vm.toggleColor();
      }
    });

    document.querySelector('.message-wrapper').addEventListener('ps-y-reach-end', () => {
      if (this.messageList.length) {
        this.isScrollAtBottom = true;
      }
      this.setTotalUnseen(0);
      this.showMessageNotif = false;
    });

    document.querySelector('.message-wrapper').addEventListener('ps-scroll-y', () => {
      this.isScrollAtBottom = false;
    });

    this.showPulseEffect = this.nonActualLesson ? true : !store.getUsefulPhraseSeen(this.userId);

    this.tooltipvisible = this.shouldShowTooltip;
    this.tooltipTextClass = this.isStudent ? "tooltiptextjp": "tooltiptexten";
  },

  methods: {
    ...mapMutations('socketio', [
      'setConnected',
      'pushMessageList',
      'removeMessage',
      'updateTextMessage',
      'setRemoteSeen',
      'setTotalUnseen',
      'updateTrainerTag'
    ]),
    ...mapMutations('trainerTag', ['addTrainerTags', 'setTrainerTags']),
    ...mapActions('trainerTag', ['saveTrainerTag']),
    ...mapActions('socketio', ['deleteMessageTag']),
    toggleTooltipVisible(){
      if (this.tooltipvisible) { this.tooltipvisible = false; }
    },
    checkSendKey: function (e) {
      // ignore if not force sending (CTRL + enter)
      if (this.isIMEComposing && !(e.ctrlKey)) {
        return;
      }

      // new line
      if (e.key === 'Enter' && e.shiftKey) {
        return;
      }

      if (this.isLessonBeyondChatPeriod) {
        return;
      }

      e.preventDefault();

      if (!this.sendMessage()) {
        e.stopPropagation();
        e.returnValue = false;
      }
    },

    // triggered when enter is pressed or send button is presed
    sendMessage: function() {
      if (this.isToolbarDisplayed) {
        this.toogleToolbar();
      }

      if (this.disabledChat) {
        return;
      }

      if (this.enableTrainerTag) {
        this.$refs.trainerTag.removeQlTagFormatting();
      }

      this.chatMessage = this.chatMessage.trim();
      this.chatMessage = this.trimExtraChatTags(this.chatMessage, '<p><br></p>');
      this.chatMessage = this.trimExtraChatTags(this.chatMessage, '<li><br></li>');

      let message = this.$refs.editor.quill.getText().trim();

      if (message.length !== 0) {
        if (this.pendingEdit) {
          this.updateMessage();
          return;
        }

        let that = this;
        let uuid = this.uuid();

        const item = {
          uuid: uuid,
          m: this.chatMessage
        };

        this.tempMessageList.push(item);
        that.$nextTick(() => that.scrollToEnd());

        // Emit+save request to socket.io backend
        this.socket.emit(
          'message',
          { type: 'new', text: this.chatMessage, uuid: uuid },
          //callback function
          async function(response, uuid) {
            if (response.success) {
              that.setRemoteSeen(false);
              // add wordtag as object in messagelist items
              response.data['wt'] = await that.processTrainerTag(response.data.id, message);
              that.socket.emit('message', {
                type: 'add-tag',
                id: response.data.id,
                wt: response.data['wt']
              });
              that.deleteTempMessage(uuid);
              that.pushMessageList(response.data);
              that.$nextTick(() => that.scrollToEnd());
            } else {
              that.$logger.error('Sending of message failed', {
                operation: 'socket:message',
                payload: response,
                event_type: 16,
                lesson_id: this.lessonId
              });
              that.pushMessageList({ ...item, f: that.accType, err: true });
              that.deleteTempMessage(uuid);
            }
          }
        );
        this.chatMessage = '';
        this.$refs.editor.quill.setContents([{ insert: '\n' }]);
        return true;
      }

      this.chatMessage = '';

      return false;
    },

    // Update message
    updateMessage: async function() {
      let that = this;

      const item = {
        id: this.pendingEdit.id,
        m: this.chatMessage
      };

      let message = this.$refs.editor.quill.getText().trim();

      // add wordtag as object in messagelist items
      item['wt'] = await that.processTrainerTag(item.id, message);

      // Emit+edit request to socket.io backend
      this.socket.emit(
        'message',
        { type: 'edit', item, word_tag: item['wt'] },
        // Callback function
        function(success) {
          if (success) {
            that.updateTextMessage(item);
          }
        }
      );
      this.chatMessage = '';
      this.pendingEdit = null;

      this.$refs.editor.quill.blur();
      this.focusOnChatInput();
    },

    // Triggered when [x] on the right of own message is clicked
    deleteMessage: function(item) {
      let that = this;

      // Emit+delete request to socket.io backend
      this.socket.emit(
        'message',
        { type: 'delete', item },
        // Callback function
        async function(success) {
          if (success) {
            if (item.wt && item.wt.length) {
              await that.deleteMessageTag(item.id);
            }
            that.removeMessage(item);
          }
        }
      );

      this.pendingEdit = null;
    },

    // Triggered when message save is success, inside Callback function
    deleteTempMessage: function(uuid) {
      let that = this;
      this.tempMessageList.forEach(function(item, index) {
        if (uuid === item.uuid) {
          that.tempMessageList.splice(index, 1);
          return;
        }
      });
    },

    // Triggered when message save is failed, inside Callback function
    updateTempMessageFailed: function(uuid) {
      let that = this;
      this.tempMessageList.forEach(function(item, index) {
        if (uuid === item.uuid) {
          let newItem = { ...item, ...{ failed: true } };
          that.tempMessageList.splice(index, 1);
          that.tempMessageList.push(newItem);
          return;
        }
      });
    },

    async processTrainerTag(messageId, message) {
      let data = await this.saveTrainerTag({ id: messageId, text: message });
      if (!data) {
        return [];
      }
      return data.map(trainerTag => {
        return {
          id: trainerTag.tagged_word.id,
          message_id: messageId,
          word: trainerTag.tagged_word.word,
          tags: trainerTag.tags
        };
      });
    },

    // scroll to bottom
    scrollToEnd: function() {
      var container = this.$el.querySelector('.message-wrapper');
      container.scrollTop = container.scrollHeight;
    },

    // send seen notification
    seenMessage: function() {
      this.socket.emit('message', { type: 'seen' });
    },

    enablingChat() {
      if (this.isLessonOngoing) {
        this.setConnected(true);
      }
    },

    trimExtraChatTags(str, extraTags) {
      const index = this.chatMessage.lastIndexOf(extraTags);
      return index >= 0 ? str.substring(0, index) + str.substring(index + extraTags.length) : str;
    },

    setPasteContentAsPlain() {
      this.$refs.editor.quill.clipboard.addMatcher(Node.ELEMENT_NODE, function(node, delta) {
        delta.forEach(e => {
          if (e.insert.image) {
            e.insert.image = '';
          }
          if (e.attributes) {
            e.attributes.color = '';
            e.attributes.background = '';
            e.attributes.header = '';
          }
        });
        return delta;
      });
    },

    toogleToolbar() {
      this.isToolbarDisplayed = !this.isToolbarDisplayed;
      if (this.isToolbarDisplayed) {
        document.getElementsByClassName('ql-toolbar')[0].style.display = 'block';
        document.querySelector('.ql-snow.ql-toolbar').classList.remove('showColors');
        this.showColors = false;
      } else {
        document.getElementsByClassName('ql-toolbar')[0].style.display = 'none';
      }
    },

    toggleColor() {
      this.showColors = !this.showColors;
      if (this.showColors) {
        document.querySelector('.ql-snow.ql-toolbar').classList.add('showColors');
      } else {
        document.querySelector('.ql-snow.ql-toolbar').classList.remove('showColors');
      }
    },

    editMessage(payload) {
      this.chatMessage = payload.m;
      this.pendingEdit = JSON.parse(JSON.stringify(payload));
      setTimeout(() => {
        const index = document.querySelector('.ql-editor.scrollbar').innerText.length;
        this.$refs.editor.quill.setSelection(index, 0);
        if (this.enableTrainerTag) {
          this.manageEditMessageTags(payload);
        }
      }, 100);
    },

    manageEditMessageTags(payload) {
      if (payload.wt.length) {
        payload.wt.forEach(tag => {
          this.addTrainerTags({
            tagged_word_id: tag.id,
            word: tag.word,
            tags: tag.tags,
            tag_ids: tag.tags.map(tag => parseInt(tag.vocabulary_tag_id))
          });
        });
        this.$refs.trainerTag.removeChangedHighlightedText();
      }

      if (!payload.wt.length) {
        this.$refs.trainerTag.setFormatPerWord();
        this.pendingEdit.m = this.chatMessage;
      }
    },

    cancelEdit() {
      this.chatMessage = '';
      this.pendingEdit = null;
      this.setTrainerTags([]);

      this.focusOnChatInput();
    },

    focusOnChatInput() {
      setTimeout(() => {
        this.$refs.editor.quill.focus();
      }, 100);
    },

    clearChatContent() {
      this.chatMessage = '';
      this.$refs.editor.quill.setText('');
      if (this.isToolbarDisplayed) {
        this.toogleToolbar();
      }
    },

    togglePhraseDialog() {
      this.hidePulsateEffect();
      const element = this.$refs.phraseModal;
      element.dialogVisible = true;
      this.isPhraseActive = element.dialogVisible;
    },

    hidePulsateEffect() {
      if (this.showPulseEffect) {
        this.showPulseEffect = false;
        if (!this.nonActualLesson) {
          store.setUsefulPhraseSeen(this.userId);
        }
      }
    },

    phraseMessage(item) {
      if (Object.keys(item).length && item.phrase_en && item.phrase_ja) {
        let en = item.phrase_en.trim();
        let ja = item.phrase_ja.trim();
        if (this.isStudent) {
          this.chatMessage += `<p>${en}</p>\n`;
        }
        if (this.isTrainer) {
          this.chatMessage = this.trimExtraChatTags(this.chatMessage, '<p><br></p>');
          this.chatMessage += `<ql-noneditable contenteditable="false">${en}\n${ja}&nbsp;</ql-noneditable>`;
        }
      }
      // Trim the chatMessage string to remove leading/trailing spaces
      this.chatMessage = this.chatMessage.trim();
    },

    addSpanElement() {
      if (this.hideUsefulPhrase) {
        return;
      }
      const length = this.$refs.editor.quill.getLength();
      if (this.$refs.phraseModal.isPreviousCharacterNonEditable(length)) {
        this.$refs.editor.quill.insertText(length, '\n');
        this.$refs.editor.quill.setSelection(length + 1);
      }
    }
  },

  watch: {
    remoteTyping: function() {
      if (this.isScrollAtBottom) {
        this.$nextTick(() => this.scrollToEnd());
      }
    },

    totalUnseen: function (newVal) {  
      if (newVal === 0) {
        this.seenMessage();
      }

      if (!this.isScrollAtBottom && newVal) {
        this.showMessageNotif = true;
      } else {
        this.$nextTick(() => this.scrollToEnd());
      }
    },

    localTyping: function() {
      this.socket.emit('message', { type: 'typing', typing: this.localTyping });
    },

    // Send typing notification on textbox change
    chatMessage: function(newVal) {
      let that = this;
      // clear setTimeout when user is still typing
      clearTimeout(this.localTypingTimer);

      if (!this.localTyping) {
        this.localTyping = true;
      }

      this.localTypingTimer = setTimeout(function() {
        that.localTyping = false;
      }, 1000);

      // remove cancel edit btn if user is currently editing
      this.isEditingActive = this.pendingEdit && newVal && this.isOriginalMessageChange;
    },

    onConnect: function(newVal) {
      if (newVal) {
        this.enablingChat();
      }
    },
    lessonHasEnded(state) {
      if (state) {
        this.clearChatContent();
      }
    },
    isPhraseActive(newVal) {
      if (newVal) {
        this.isToolbarDisplayed = newVal;
        this.toogleToolbar();
      }
    },
    isLessonBecameOngoing(val) {
      if (val && this.isTrainer && this.chatMessage.length) {
        this.$refs.trainerTag.setFormatPerWord();
      }

      if (val && this.isAssessmentStudent) {
        this.tooltipvisible = true;
      }
    },
    
    disabledChat(val) {
      if (val) {
        let reason = this.isLessonBeyondChatPeriod
          ? 'Beyond chat period'
          : this.isWaitingForLesson
          ? 'Waiting for lesson'
          : this.voluntaryLessonTermination
          ? 'Voluntary lesson termination'
          : !this.connected
          ? 'Socket disconnected'
          : null;

        this.$logger.info('Chat is disabled', { reason });
      }
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped src="@/assets/css/pc/chatbox.css"></style>
<style scoped src="@/assets/css/sp/chatbox.css"></style>
<style scoped>
.tooltip {
  position: relative;
  display: inline-block;
}

.tooltip .tooltiptextjp {
  width: 250px;
  background-color: white;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 3;
  top: calc(-100% - 193px);
  left: 60px;
  margin-left: -60px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.tooltip .tooltiptextjp::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 14%;
  transform: translateX(-50%);
  border-width: 29px 8px 0 8px;
  border-style: solid;
  border-color: white transparent transparent transparent;
}
@media screen and (max-width: 960px) {
  .tooltip .tooltiptextjp {
    top: calc(-100% - 210px);
    left: 70px;
  }
}

.tooltip .tooltiptexten {
  width: 250px;
  background-color: white;
  text-align: center;
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 3;
  top: calc(-100% - 175px);
  left: 60px;
  margin-left: -60px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.tooltip .tooltiptexten::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 14%;
  transform: translateX(-50%);
  border-width: 29px 8px 0 8px;
  border-style: solid;
  border-color: white transparent transparent transparent;
}
@media screen and (max-width: 960px) {
  .tooltip .tooltiptexten {
    top: calc(-100% - 194px);
    left:70px;
  }
}
.chatSupportTextClass {
  text-align: center; 
  margin-bottom: 10px; 
  margin-top: 15px; 
  font-size: large; 
  padding: 7px;
}

.messageSendTextClass {
  text-align: center; 
  margin-bottom: 5px; 
  padding-left: 7px; 
  padding-right: 7px; 
  line-height: 1.5;
}

.btn-click {
  background-color: #1287cd;
  color: white;
}
</style>
